import type { NudgeSource } from '@cobliteam/hooks/src/useNudge/types';
import useNudges from '@cobliteam/hooks/src/useNudge/useNudges';
import { AnimatePresence, MotionConfig, motion, type Transition } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import NudgeCard from './NudgeCard';
import NudgeCollapsedRow from './NudgeCollapsedRow';

const LAYOUT_TRANSITION: Transition = { type: 'spring', bounce: 0, duration: 0.3 };
const FADE_TRANSITION: Transition = { duration: 0.18, ease: 'easeOut' };

export interface NudgeFloatingStackProps {
  /**
   * MUST be a stable, module-level array — each entry is a hook. See `useNudges`.
   */
  sources: NudgeSource[];
  /** Total cap across every kind. Extra nudges are dropped, not paginated. */
  limit: number;
}

/**
 * The floating nudge surface, bottom-left above the collapsed sidebar.
 *
 * Accordion: the top-ranked nudge is expanded, the rest sit below as one-line rows the user can
 * click to bring forward. Four expanded cards would run past 600px and swallow the left side of
 * the screen, so only one body is mounted at a time — and there is still no carousel, since every
 * nudge stays on screen and reachable in one click.
 */
const NudgeFloatingStack = ({ sources, limit }: NudgeFloatingStackProps) => {
  const nudges = useNudges(sources, limit);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  /*
   * Falls back to the top-ranked nudge that does NOT defer work, whenever nothing is pinned open or
   * the pinned one has left the stack (dismissed, or its source turned ineligible).
   *
   * A nudge declaring `onExpand` is skipped by that fallback on purpose: auto-expanding it would
   * run the work it deliberately deferred, and on most pages it would be the only nudge around. So
   * a stack of nothing but deferred nudges renders fully collapsed until the user picks one.
   */
  const pinned = nudges.find((nudge) => nudge.id === expandedId);
  const expandedNudge = pinned ?? nudges.find((nudge) => !nudge.onExpand);

  /*
   * Fires once per pinned nudge, keyed by ID.
   *
   * `pinned` is a fresh object every render and its `onExpand` a fresh closure, so depending on the
   * callback would re-run this on every render — harmless while `onExpand` is an idempotent
   * `setExpanded(true)`, but the contract promises "called when the user expands", and the first
   * source to put a `track()` or an imperative `refetch()` there would fire it per render. The ref
   * keeps the latest callback without making it a dependency.
   */
  const onExpandRef = useRef(pinned?.onExpand);
  onExpandRef.current = pinned?.onExpand;

  const pinnedId = pinned?.id;
  useEffect(() => {
    if (pinnedId) onExpandRef.current?.();
  }, [pinnedId]);

  return (
    <MotionConfig reducedMotion='user'>
      {/* The outer presence lets the last dismissed nudge animate out instead of vanishing with the stack. */}
      <AnimatePresence>
        {nudges.length > 0 && (
          <motion.div
            key='nudge-floating-stack'
            data-testid='nudge-floating-stack'
            exit={{ opacity: 0 }}
            transition={FADE_TRANSITION}
            className='fixed bottom-4 left-[calc(var(--sidebar-width-icon)+1rem)] z-[1003] flex w-fit max-w-[360px] flex-col gap-2'
          >
            <AnimatePresence mode='popLayout'>
              {nudges.map((nudge) => {
                const isExpanded = nudge.id === expandedNudge?.id;
                return (
                  <motion.div
                    key={nudge.id}
                    layout='position'
                    className='relative'
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -16, transition: FADE_TRANSITION }}
                    transition={LAYOUT_TRANSITION}
                  >
                    {/* popLayout takes the outgoing shape out of flow, so card and row crossfade in place while siblings glide. */}
                    <AnimatePresence mode='popLayout' initial={false}>
                      <motion.div
                        key={isExpanded ? 'expanded' : 'collapsed'}
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        transition={FADE_TRANSITION}
                        style={{ transformOrigin: 'bottom left' }}
                      >
                        {isExpanded ? <NudgeCard nudge={nudge} /> : <NudgeCollapsedRow nudge={nudge} onExpand={() => setExpandedId(nudge.id)} />}
                      </motion.div>
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </MotionConfig>
  );
};

export default NudgeFloatingStack;
